// eslint-disable-next-line eslint-comments/disable-enable-pair
/* eslint-disable react/no-array-index-key */
import React, { Fragment, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Select from './bootstrap/forms/Select';
import FormGroup from './bootstrap/forms/FormGroup';
import InputGroup, { InputGroupText } from './bootstrap/forms/InputGroup';
import Button from './bootstrap/Button';
import { CardLabel, CardTitle } from './bootstrap/Card';
import Input from './bootstrap/forms/Input';
import GooglePlaceAddress from './GooglePlaceAddress';
import Textarea from './bootstrap/forms/Textarea';
import Checks from './bootstrap/forms/Checks';
import FinancingMonthlyEstimateModal from './FinancingMonthlyEstimateModal';
import {
	EKHO_FINANCING_APPLICATION_CONSTANTS,
	COLORS,
	EKHO_LOAN_TERM_OPTIONS,
	CREDIT_APP_FIELD_IDS,
	EKHO_FINANCING_APPLICATION_SECTIONS,
} from '../utils/constants';
import { priceFormat, priceFormatNoCents } from '../helpers/helpers';
import { generateMonthlyPaymentEstimateObject } from '../utils/util_funcs';
import PriceSuffix from './PriceSuffix';
// import { fetchGoogleMapsApiKey } from '../../../../../serverCalls';
// import MobileViewContext from '../../../../../contexts/mobileViewContext';

const ApplicantDetailsForm = ({
	detailsFormik,
	employmentFormik,
	isCoApplicant,
	mapsKey,
	mobileView,
	includePersonalReferences,
	includeLoanConfig,
	customOnBlurFunc,
	fieldsToHide,
	// setFinanceModalOpen,
	minDownPayment,
	maxDownPayment,
	allFieldsFullWidth,
	vehiclePrice,
	financingProgram,
}) => {
	const [activeLoanTerm, setActiveLoanTerm] = useState(detailsFormik.values.loanTerm);
	const [financingEstimateModalOpen, setFinancingEstimateModalOpen] = useState(false);
	const [selectedFinancingEstimateData, setSelectedFinancingEstimateData] = useState(null);
	// Use useEffect to synchronize state with Formik values
	useEffect(() => {
		setActiveLoanTerm(detailsFormik.values.loanTerm);
	}, [detailsFormik.values.loanTerm]);

	useEffect(() => {
		if (vehiclePrice && financingProgram) {
			const ekhoFinancingEstObject = generateMonthlyPaymentEstimateObject(
				vehiclePrice,
				financingProgram,
				null,
				undefined,
				detailsFormik.values.downpaymentAmt,
				detailsFormik.values.loanTerm,
			);
			setSelectedFinancingEstimateData(ekhoFinancingEstObject);
		}
	}, [
		vehiclePrice,
		financingProgram,
		detailsFormik.values.downpaymentAmt,
		detailsFormik.values.loanTerm,
	]);

	let downPaymentFormText = 'A higher down payment may increase your likelihood of approval. ';
	if (minDownPayment > 0) {
		downPaymentFormText += `Minimum: ${priceFormat(minDownPayment)}`;
	}
	if (maxDownPayment) {
		if (minDownPayment > 0) {
			downPaymentFormText += ', ';
		}
		downPaymentFormText += `Maximum: ${priceFormat(maxDownPayment)}`;
	}

	const getIncomeTypeKey = (value) =>
		Object.entries(EKHO_FINANCING_APPLICATION_CONSTANTS.INCOME_TYPE_OPTIONS).find(
			([, val]) => val === value,
		)?.[0] || null;

	function setOnBlurFunc(formik) {
		if (customOnBlurFunc) {
			return (e) => {
				customOnBlurFunc(e, formik);
			};
		}
		return formik.handleBlur;
	}

	// financing details formik
	return (
		<>
			{includeLoanConfig && (
				<>
					{selectedFinancingEstimateData && (
						<FinancingMonthlyEstimateModal
							financingEstimateModalOpen={financingEstimateModalOpen}
							setFinancingEstimateModalOpen={setFinancingEstimateModalOpen}
							financingEstimateData={selectedFinancingEstimateData}
						/>
					)}
					<CardLabel>
						<CardTitle tag='h4' className='h4 d-flex'>
							{EKHO_FINANCING_APPLICATION_SECTIONS.PREFERRED_TERMS}
							{selectedFinancingEstimateData && (
								<PriceSuffix
									onClickFunc={() => setFinancingEstimateModalOpen(true)}
									suffixText={`est. ${priceFormatNoCents(
										selectedFinancingEstimateData?.monthlyPayment,
									)}/mo`}
									showInfoIcon
									mobileView={mobileView}
								/>
							)}
						</CardTitle>
					</CardLabel>
					<FormGroup id='loanTerm' label='Loan term (months)'>
						<div className='row g-3'>
							{Object.values(EKHO_LOAN_TERM_OPTIONS).map((loanTerm) => (
								<div className='col-auto'>
									<Button
										color='info'
										size='lg'
										key={`${loanTerm}`}
										isLight={loanTerm !== activeLoanTerm}
										onClick={() => {
											detailsFormik.setFieldValue('loanTerm', loanTerm);
										}}>
										{`${loanTerm} mo`}
									</Button>
								</div>
							))}
						</div>
					</FormGroup>
					<div
						className={classNames({ 'col-12': true, 'col-sm-6': !allFieldsFullWidth })}>
						<FormGroup
							id='downpaymentAmt'
							label='Down payment amount (dollars)'
							formText={downPaymentFormText}>
							<InputGroup id='downpaymentAmt' label='Down payment amount (dollars)'>
								<InputGroupText id='downpaymentAmt'>$</InputGroupText>
								<Input
									id='downpaymentAmt'
									size='lg'
									type='number'
									placeholder=''
									required
									name='downpaymentAmt'
									min={minDownPayment}
									max={maxDownPayment}
									onChange={detailsFormik.handleChange}
									onBlur={setOnBlurFunc(detailsFormik)}
									value={detailsFormik.values.downpaymentAmt}
									isValid={detailsFormik.isValid}
									isTouched={detailsFormik.touched.downpaymentAmt}
									invalidFeedback={detailsFormik.errors.downpaymentAmt}
								/>
							</InputGroup>
						</FormGroup>
					</div>
				</>
			)}
			<CardLabel>
				<CardTitle tag='h4' className='h4'>
					{isCoApplicant
						? `Co-applicant's ${EKHO_FINANCING_APPLICATION_SECTIONS.PERSONAL_DETAILS}`
						: EKHO_FINANCING_APPLICATION_SECTIONS.PERSONAL_DETAILS}
				</CardTitle>
			</CardLabel>
			{!fieldsToHide.includes(CREDIT_APP_FIELD_IDS.DOB) && (
				<div className={classNames({ 'col-12': true, 'col-sm-6': !allFieldsFullWidth })}>
					<FormGroup id='dob' label='Date of birth'>
						<Input
							size='lg'
							placeholder='MM/DD/YYYY'
							type={mobileView ? 'text' : 'date'}
							mask={mobileView ? '99/99/9999' : undefined}
							required
							onChange={detailsFormik.handleChange}
							onBlur={setOnBlurFunc(detailsFormik)}
							value={detailsFormik.values.dob}
							isValid={detailsFormik.isValid}
							isTouched={detailsFormik.touched.dob}
							invalidFeedback={detailsFormik.errors.dob}
						/>
					</FormGroup>
				</div>
			)}
			{!fieldsToHide.includes(CREDIT_APP_FIELD_IDS.PHONE_NUMBER) && (
				<div className={classNames({ 'col-12': true, 'col-sm-6': !allFieldsFullWidth })}>
					<FormGroup id='phoneNumber' label='Phone number'>
						<Input
							type='tel'
							size='lg'
							placeholder=''
							autoComplete='tel'
							mask='(999) 999-9999'
							required
							onChange={detailsFormik.handleChange}
							onBlur={setOnBlurFunc(detailsFormik)}
							value={detailsFormik.values.phoneNumber}
							isValid={detailsFormik.isValid}
							isTouched={detailsFormik.touched.phoneNumber}
							invalidFeedback={detailsFormik.errors.phoneNumber}
						/>
					</FormGroup>
				</div>
			)}
			{!fieldsToHide.includes(CREDIT_APP_FIELD_IDS.SSN) && (
				<div className={classNames({ 'col-12': true, 'col-sm-6': !allFieldsFullWidth })}>
					<FormGroup
						id='ssn'
						label='Social Security Number'
						formText='Encrypted upon submission'>
						<Input
							type='tel'
							size='lg'
							placeholder=''
							autoComplete='tel'
							mask='999-99-9999'
							required
							onChange={detailsFormik.handleChange}
							onBlur={detailsFormik.handleBlur}
							value={detailsFormik.values.ssn}
							isValid={detailsFormik.isValid}
							isTouched={detailsFormik.touched.ssn}
							invalidFeedback={detailsFormik.errors.ssn}
						/>
					</FormGroup>
				</div>
			)}
			{!fieldsToHide.includes(CREDIT_APP_FIELD_IDS.MARITAL_STATUS) && (
				<div className={classNames({ 'col-12': true, 'col-sm-6': !allFieldsFullWidth })}>
					<FormGroup id='maritalStatus' label='Marital status'>
						<Select
							ariaLabel='Marital status'
							size='lg'
							name='maritalStatus'
							placeholder='Select'
							list={[
								{ value: 'Single', text: 'Single' },
								{ value: 'Married', text: 'Married' },
								{ value: 'Widowed', text: 'Widowed' },
								{ value: 'Divorced', text: 'Divorced' },
								{ value: 'Separated', text: 'Separated' },
							]}
							onChange={detailsFormik.handleChange}
							onBlur={setOnBlurFunc(detailsFormik)}
							value={detailsFormik.values.maritalStatus}
							isValid={detailsFormik.isValid}
							isTouched={detailsFormik.touched.maritalStatus}
							invalidFeedback={detailsFormik.errors.maritalStatus}
						/>
					</FormGroup>
				</div>
			)}
			<div className='col-12 pb-30' />
			<CardLabel>
				<CardTitle tag='h5' className='h5' style={{ fontWeight: '600' }}>
					{isCoApplicant ? "Co-applicant's home address" : 'Your home address'}
				</CardTitle>
			</CardLabel>
			<GooglePlaceAddress
				googleMapsApiKey={mapsKey}
				inputLabelString='Home address'
				formik={detailsFormik}
				lockedFields={new Set()}
				isSmall={false}
				fieldNames={{
					addressString: 'addressString',
					addressLineOne: 'addressLineOne',
					addressLineTwo: 'addressLineTwo',
					city: 'city',
					county: 'county',
					state: 'state',
					zip: 'zip',
				}}
			/>
			{!fieldsToHide.includes(CREDIT_APP_FIELD_IDS.HOME_ADDRESS_SAME_AS_ID_ADDRESS) && (
				<div className='col-12'>
					<FormGroup id='homeAddressSameAsIdAddress' label='' labelClassName='pb-2'>
						<Checks
							label='Address on my state-issued ID is the same as the address listed above'
							name='homeAddressSameAsIdAddress'
							checked={detailsFormik.values.homeAddressSameAsIdAddress}
							onChange={detailsFormik.handleChange}
							onBlur={setOnBlurFunc(detailsFormik)}
							isValid={detailsFormik.isValid}
							isTouched={detailsFormik.touched.homeAddressSameAsIdAddress}
							invalidFeedback={detailsFormik.errors.homeAddressSameAsIdAddress}
						/>
					</FormGroup>
				</div>
			)}
			{/* Add a checkbox asking if the address on their state-issued ID document (that you plan to use for ID verification) is the same as the address listed above */}
			<div className='col-12 pb-30' />
			<CardLabel>
				<CardTitle tag='h5' className='h5' style={{ fontWeight: '600' }}>
					{isCoApplicant ? "Co-applicant's housing details" : 'Your housing details'}
				</CardTitle>
			</CardLabel>
			{!fieldsToHide.includes(CREDIT_APP_FIELD_IDS.HOUSING_OWNERSHIP) && (
				<div className={classNames({ 'col-12': true, 'col-sm-6': !allFieldsFullWidth })}>
					<FormGroup id='housingOwnership' label='Housing ownership type'>
						<Select
							ariaLabel='Housing ownership type'
							placeholder='Select'
							size='lg'
							required
							list={[
								{ value: 'Owned', text: 'Own' },
								{ value: 'Rented', text: 'Rent' },
								{ value: 'Family', text: 'Family' },
								{ value: 'LivesWithRelatives', text: 'Live with Relatives' },
							]}
							onChange={detailsFormik.handleChange}
							onBlur={setOnBlurFunc(detailsFormik)}
							value={detailsFormik.values.housingOwnership}
							isValid={detailsFormik.isValid}
							isTouched={detailsFormik.touched.housingOwnership}
							invalidFeedback={detailsFormik.errors.housingOwnership}
						/>
					</FormGroup>
				</div>
			)}
			{!fieldsToHide.includes(CREDIT_APP_FIELD_IDS.HOME_MOVE_IN_DATE) && (
				<div className={classNames({ 'col-12': true, 'col-sm-6': !allFieldsFullWidth })}>
					<FormGroup id='homeMoveInDate' label='When did you move in?'>
						<Input
							size='lg'
							placeholder='MM/DD/YYYY'
							autoComplete='home-move-in-date'
							type={mobileView ? 'text' : 'date'}
							mask={mobileView ? '99/99/9999' : undefined}
							required
							onChange={detailsFormik.handleChange}
							onBlur={setOnBlurFunc(detailsFormik)}
							value={detailsFormik.values.homeMoveInDate}
							isValid={detailsFormik.isValid}
							isTouched={detailsFormik.touched.homeMoveInDate}
							invalidFeedback={detailsFormik.errors.homeMoveInDate}
						/>
					</FormGroup>
				</div>
			)}
			{!fieldsToHide.includes(CREDIT_APP_FIELD_IDS.MONTHLY_AMT) && (
				<div className={classNames({ 'col-12': true, 'col-sm-6': !allFieldsFullWidth })}>
					<FormGroup id='monthlyAmt' label='Monthly housing spend (dollars)'>
						<InputGroup id='monthlyAmt' label='Monthly housing spend (dollars)'>
							<InputGroupText id='monthlyAmt'>$</InputGroupText>
							<Input
								size='lg'
								type='number'
								placeholder=''
								required
								name='monthlyAmt'
								onChange={detailsFormik.handleChange}
								onBlur={setOnBlurFunc(detailsFormik)}
								value={detailsFormik.values.monthlyAmt}
								isValid={detailsFormik.isValid}
								isTouched={detailsFormik.touched.monthlyAmt}
								invalidFeedback={detailsFormik.errors.monthlyAmt}
							/>
						</InputGroup>
					</FormGroup>
				</div>
			)}
			<div className='col-12 pb-30' />
			<CardLabel>
				<CardTitle tag='h4' className='h4'>
					{isCoApplicant
						? `Co-applicant's ${EKHO_FINANCING_APPLICATION_SECTIONS.EMPLOYMENT_INFO}`
						: EKHO_FINANCING_APPLICATION_SECTIONS.EMPLOYMENT_INFO}
				</CardTitle>
			</CardLabel>
			{!fieldsToHide.includes(CREDIT_APP_FIELD_IDS.EMPLOYMENT_STATUS) && (
				<div className={classNames({ 'col-12': true, 'col-sm-6': !allFieldsFullWidth })}>
					<FormGroup id='employmentStatus' label='Employment status'>
						<Select
							ariaLabel='Employment status'
							placeholder='Select'
							size='lg'
							required
							list={[
								{ value: 'Employed', text: 'Employed' },
								{ value: 'SelfEmployed', text: 'Self-Employed' },
								{ value: 'Retired', text: 'Retired' },
							]}
							onChange={employmentFormik.handleChange}
							onBlur={setOnBlurFunc(employmentFormik)}
							value={employmentFormik.values.employmentStatus}
							isValid={employmentFormik.isValid}
							isTouched={employmentFormik.touched.employmentStatus}
							invalidFeedback={employmentFormik.errors.employmentStatus}
						/>
					</FormGroup>
				</div>
			)}
			{!fieldsToHide.includes(CREDIT_APP_FIELD_IDS.MONTHLY_GROSS_INCOME) && (
				<div className={classNames({ 'col-12': true, 'col-sm-6': !allFieldsFullWidth })}>
					<FormGroup id='monthlyGrossIncome' label='Monthly gross income (dollars)'>
						<InputGroup id='monthlyGrossIncome'>
							<InputGroupText id='monthlyGrossIncome'>$</InputGroupText>
							<Input
								name='monthlyGrossIncome'
								size='lg'
								type='number'
								onChange={(e) => {
									// const newVal = e.target.value.replace(/^0+/, '');
									employmentFormik.setFieldValue(
										'monthlyGrossIncome',
										parseInt(e.target.value, 10), // OK to use parseInt here for monthly income
									);
								}}
								onBlur={setOnBlurFunc(employmentFormik)}
								value={employmentFormik.values.monthlyGrossIncome}
								isValid={employmentFormik.isValid}
								isTouched={employmentFormik.touched.monthlyGrossIncome}
								invalidFeedback={employmentFormik.errors.monthlyGrossIncome}
							/>
						</InputGroup>
					</FormGroup>
				</div>
			)}
			{(employmentFormik.values.employmentStatus === 'Employed' ||
				employmentFormik.values.employmentStatus === 'SelfEmployed') && (
				<>
					{!fieldsToHide.includes(CREDIT_APP_FIELD_IDS.EMPLOYER_NAME) && (
						<div
							className={classNames({
								'col-12': true,
								'col-sm-6': !allFieldsFullWidth,
							})}>
							<FormGroup id='employerName' label='Employer name'>
								<Input
									size='lg'
									placeholder=''
									type='text'
									onChange={employmentFormik.handleChange}
									onBlur={setOnBlurFunc(employmentFormik)}
									value={employmentFormik.values.employerName}
									isValid={employmentFormik.isValid}
									isTouched={employmentFormik.touched.employerName}
									invalidFeedback={employmentFormik.errors.employerName}
								/>
							</FormGroup>
						</div>
					)}
					{!fieldsToHide.includes(CREDIT_APP_FIELD_IDS.EMPLOYER_PHONE_NUMBER) && (
						<div
							className={classNames({
								'col-12': true,
								'col-sm-6': !allFieldsFullWidth,
							})}>
							<FormGroup id='employerPhoneNumber' label='Employer phone number'>
								<Input
									size='lg'
									type='tel'
									placeholder=''
									autoComplete='tel'
									mask='(999) 999-9999'
									required
									onChange={employmentFormik.handleChange}
									onBlur={setOnBlurFunc(employmentFormik)}
									value={employmentFormik.values.employerPhoneNumber}
									isValid={employmentFormik.isValid}
									isTouched={employmentFormik.touched.employerPhoneNumber}
									invalidFeedback={employmentFormik.errors.employerPhoneNumber}
								/>
							</FormGroup>
						</div>
					)}
					{!fieldsToHide.includes(CREDIT_APP_FIELD_IDS.OCCUPATION) && (
						<div
							className={classNames({
								'col-12': true,
								'col-sm-6': !allFieldsFullWidth,
							})}>
							<FormGroup id='occupation' label='Occupation'>
								<Input
									size='lg'
									placeholder=''
									type='text'
									onChange={employmentFormik.handleChange}
									onBlur={setOnBlurFunc(employmentFormik)}
									value={employmentFormik.values.occupation}
									isValid={employmentFormik.isValid}
									isTouched={employmentFormik.touched.occupation}
									invalidFeedback={employmentFormik.errors.occupation}
								/>
							</FormGroup>
						</div>
					)}
					{!fieldsToHide.includes(CREDIT_APP_FIELD_IDS.JOB_START_DATE) && (
						<div
							className={classNames({
								'col-12': true,
								'col-sm-6': !allFieldsFullWidth,
							})}>
							<FormGroup id='jobStartDate' label='Employment start date'>
								<Input
									size='lg'
									placeholder='MM/DD/YYYY'
									mask={mobileView ? '99/99/9999' : undefined}
									autoComplete='job-start-date'
									type={mobileView ? 'text' : 'date'}
									required
									onChange={employmentFormik.handleChange}
									onBlur={setOnBlurFunc(employmentFormik)}
									value={employmentFormik.values.jobStartDate || ''}
									isValid={employmentFormik.isValid}
									isTouched={employmentFormik.touched.jobStartDate}
									invalidFeedback={employmentFormik.errors.jobStartDate}
								/>
							</FormGroup>
						</div>
					)}
					<div className='col-12 pb-30' />
					<CardLabel>
						<CardTitle tag='h5' className='h5' style={{ fontWeight: '600' }}>
							{isCoApplicant
								? "Co-applicant's employer's address"
								: "Your employer's address"}
						</CardTitle>
					</CardLabel>
					<GooglePlaceAddress
						googleMapsApiKey={mapsKey}
						inputLabelString='Employer address'
						formik={employmentFormik}
						lockedFields={new Set()}
						isSmall={false}
						fieldNames={{
							addressString: 'addressString',
							addressLineOne: 'addressLineOne',
							addressLineTwo: 'addressLineTwo',
							city: 'city',
							county: 'county',
							state: 'state',
							zip: 'zip',
						}}
					/>
				</>
			)}
			<div className='col-12 pb-30' />
			<CardLabel>
				<CardTitle tag='h4' className='h4'>
					{EKHO_FINANCING_APPLICATION_SECTIONS.OTHER_SOURCES_OF_INCOME}
				</CardTitle>
			</CardLabel>
			<p className='text-muted'>
				Please list any other sources of income (e.g., spousal, rental, investment, pension,
				etc.) that you or your household have.
			</p>
			{employmentFormik.values.otherIncomes &&
				employmentFormik.values.otherIncomes.length > 0 &&
				employmentFormik.values.otherIncomes.map((field, index) => (
					<Fragment key={`${field}-${index}`}>
						<CardLabel>
							<CardTitle tag='h5' className='h5' style={{ fontWeight: '600' }}>
								Additional income {index + 1}
							</CardTitle>
						</CardLabel>
						<div
							className={classNames({
								'col-12': allFieldsFullWidth,
								'col-6': !allFieldsFullWidth,
							})}>
							<FormGroup
								id={`otherIncomes[${index}].source`}
								label='Income source'
								formText={
									employmentFormik.values.otherIncomes &&
									employmentFormik.values.otherIncomes[index].source
										? EKHO_FINANCING_APPLICATION_CONSTANTS
												.INCOME_TYPE_DESCRIPTIONS[
												getIncomeTypeKey(
													employmentFormik.values.otherIncomes[index]
														.source,
												)
										  ]
										: ''
								}>
								<Select
									ariaLabel={`otherIncomes[${index}].source`}
									placeholder='Select'
									size='lg'
									required
									list={[
										{
											value: EKHO_FINANCING_APPLICATION_CONSTANTS
												.INCOME_TYPE_OPTIONS.SPOUSAL_INCOME,
											text: 'Spousal income',
										},

										{
											value: EKHO_FINANCING_APPLICATION_CONSTANTS
												.INCOME_TYPE_OPTIONS.CHILD_SUPPORT,
											text: 'Child Support',
										},
										{
											value: EKHO_FINANCING_APPLICATION_CONSTANTS
												.INCOME_TYPE_OPTIONS.DISABILITY,
											text: 'Disability',
										},
										{
											value: EKHO_FINANCING_APPLICATION_CONSTANTS
												.INCOME_TYPE_OPTIONS.INVESTMENT,
											text: 'Investment income',
										},
										{
											value: EKHO_FINANCING_APPLICATION_CONSTANTS
												.INCOME_TYPE_OPTIONS.PENSION,
											text: 'Pension income',
										},
										{
											value: EKHO_FINANCING_APPLICATION_CONSTANTS
												.INCOME_TYPE_OPTIONS.RENTAL,
											text: 'Rental income',
										},
										{
											value: EKHO_FINANCING_APPLICATION_CONSTANTS
												.INCOME_TYPE_OPTIONS.RETIREMENT_INCOME,
											text: 'Retirement income',
										},
										{
											value: EKHO_FINANCING_APPLICATION_CONSTANTS
												.INCOME_TYPE_OPTIONS.SOCIAL_SECURITY,
											text: 'Social Security income',
										},
										{
											value: EKHO_FINANCING_APPLICATION_CONSTANTS
												.INCOME_TYPE_OPTIONS.ALIMONY,
											text: 'Alimony income',
										},
										{
											value: EKHO_FINANCING_APPLICATION_CONSTANTS
												.INCOME_TYPE_OPTIONS.UNEMPLOYMENT,
											text: 'Unemployment benefits income',
										},
										{
											value: EKHO_FINANCING_APPLICATION_CONSTANTS
												.INCOME_TYPE_OPTIONS.OTHER,
											text: 'Other income',
										},
									]}
									onChange={employmentFormik.handleChange}
									onBlur={setOnBlurFunc(employmentFormik)}
									value={employmentFormik.values.otherIncomes[index].source}
									isValid={employmentFormik.isValid}
									isTouched={
										employmentFormik.touched.otherIncomes &&
										employmentFormik.touched.otherIncomes[index] &&
										employmentFormik.touched.otherIncomes[index].source
									}
									invalidFeedback={
										employmentFormik.errors.otherIncomes &&
										employmentFormik.errors.otherIncomes[index] &&
										employmentFormik.errors.otherIncomes[index].source
									}
								/>
							</FormGroup>
						</div>
						<div
							className={classNames({
								'col-12': allFieldsFullWidth,
								'col-6': !allFieldsFullWidth,
							})}>
							<FormGroup
								id={`otherIncomes[${index}].amount`}
								label='Monthly gross income (dollars)'>
								<Input
									size='lg'
									type='number'
									placeholder=''
									required
									onChange={employmentFormik.handleChange}
									onBlur={setOnBlurFunc(employmentFormik)}
									value={employmentFormik.values.otherIncomes[index].amount}
									isValid={employmentFormik.isValid}
									isTouched={
										employmentFormik.touched.otherIncomes &&
										employmentFormik.touched.otherIncomes[index] &&
										employmentFormik.touched.otherIncomes[index].amount
									}
									invalidFeedback={
										employmentFormik.errors.otherIncomes &&
										employmentFormik.errors.otherIncomes[index] &&
										employmentFormik.errors.otherIncomes[index].amount
									}
								/>
							</FormGroup>
						</div>
						{/* make this a col-2 with diplay flex and justify-content flex-end  */}
						<div className='col-12 mt-4'>
							<Button
								color='danger'
								size='md'
								id={`removeIncome-${index}`}
								isOutline
								onClick={() => {
									const updatedIncomes =
										employmentFormik.values.otherIncomes.filter(
											(income, i) => i !== index,
										);
									employmentFormik.setFieldValue('otherIncomes', updatedIncomes);
								}}>
								Remove additional income {index + 1}
							</Button>
						</div>
					</Fragment>
				))}
			<div className='col-12 pb-30' />
			{(!employmentFormik.values.otherIncomes ||
				employmentFormik.values.otherIncomes?.length) < 6 && (
				<div className={classNames({ 'col-12': true, 'col-sm-6': !allFieldsFullWidth })}>
					<Button
						color={COLORS.INFO.name}
						size='md'
						isOutline
						onClick={() =>
							employmentFormik.setFieldValue(
								'otherIncomes',
								employmentFormik.values.otherIncomes
									? [
											...employmentFormik.values.otherIncomes,
											{ source: '', amount: 1000 },
									  ]
									: [{ source: '', amount: 1000 }],
							)
						}>
						Add additional income source
					</Button>
				</div>
			)}
			<div className='col-12 pb-30' />
			{includePersonalReferences && (
				<>
					<CardLabel>
						<CardTitle tag='h4' className='h4'>
							Personal references
						</CardTitle>
					</CardLabel>
					<p className='text-muted'>
						Please provide the names and phone numbers of two personal references.
					</p>
					{detailsFormik.values.references &&
						detailsFormik.values.references.length > 0 &&
						detailsFormik.values.references.map((field, index) => (
							<Fragment key={`${field}-${index}`}>
								<CardLabel>
									<CardTitle
										tag='h6'
										className='h6'
										style={{ fontWeight: '600' }}>
										Reference {index + 1}
									</CardTitle>
								</CardLabel>
								<div className='col-6'>
									<FormGroup
										id={`references[${index}].relationship`}
										label='Relationship with the reference'>
										<Select
											ariaLabel={`references[${index}].relationship`}
											size='lg'
											name={`references[${index}].relationship`}
											placeholder='Select'
											list={[
												{
													value: EKHO_FINANCING_APPLICATION_CONSTANTS
														.REFERENCE_RELATIONSHIP_OPTIONS.EMPLOYER,
													text: 'Employer',
												},
												{
													value: EKHO_FINANCING_APPLICATION_CONSTANTS
														.REFERENCE_RELATIONSHIP_OPTIONS.PARENT,
													text: 'Parent',
												},
												{
													value: EKHO_FINANCING_APPLICATION_CONSTANTS
														.REFERENCE_RELATIONSHIP_OPTIONS.RELATIVE,
													text: 'Relative',
												},
												{
													value: EKHO_FINANCING_APPLICATION_CONSTANTS
														.REFERENCE_RELATIONSHIP_OPTIONS.SPOUSE,
													text: 'Partner',
												},
												{
													value: EKHO_FINANCING_APPLICATION_CONSTANTS
														.REFERENCE_RELATIONSHIP_OPTIONS.FRIEND,
													text: 'Friend',
												},
												{
													value: EKHO_FINANCING_APPLICATION_CONSTANTS
														.REFERENCE_RELATIONSHIP_OPTIONS.OTHER,
													text: 'Other',
												},
											]}
											onChange={detailsFormik.handleChange}
											onBlur={setOnBlurFunc(detailsFormik)}
											value={
												detailsFormik.values.references[index].relationship
											}
											isValid={detailsFormik.isValid}
											isTouched={
												detailsFormik.touched.references &&
												detailsFormik.touched.references[index] &&
												detailsFormik.touched.references[index].relationship
											}
											invalidFeedback={
												detailsFormik.errors.references &&
												detailsFormik.errors.references[index] &&
												detailsFormik.errors.references[index].relationship
											}
										/>
									</FormGroup>
								</div>
								<div className='col-6'>
									<FormGroup
										id={`references[${index}].fullName`}
										label='Full name'>
										<Input
											size='lg'
											placeholder=''
											type='text'
											required
											onChange={detailsFormik.handleChange}
											onBlur={setOnBlurFunc(detailsFormik)}
											value={detailsFormik.values.references[index].fullName}
											isValid={detailsFormik.isValid}
											isTouched={
												detailsFormik.touched.references &&
												detailsFormik.touched.references[index] &&
												detailsFormik.touched.references[index].fullName
											}
											invalidFeedback={
												detailsFormik.errors.references &&
												detailsFormik.errors.references[index] &&
												detailsFormik.errors.references[index].fullName
											}
										/>
									</FormGroup>
								</div>
								<div className='col-6'>
									<FormGroup
										id={`references[${index}].phoneNumber`}
										label='Phone number'>
										<Input
											size='lg'
											type='tel'
											placeholder=''
											autoComplete='tel'
											mask='(999) 999-9999'
											required
											onChange={detailsFormik.handleChange}
											onBlur={setOnBlurFunc(detailsFormik)}
											value={
												detailsFormik.values.references[index].phoneNumber
											}
											isValid={detailsFormik.isValid}
											isTouched={
												detailsFormik.touched.references &&
												detailsFormik.touched.references[index] &&
												detailsFormik.touched.references[index].phoneNumber
											}
											invalidFeedback={
												detailsFormik.errors.references &&
												detailsFormik.errors.references[index] &&
												detailsFormik.errors.references[index].phoneNumber
											}
										/>
									</FormGroup>
								</div>
							</Fragment>
						))}
					<div className='col-12 pb-30' />
				</>
			)}
			<CardLabel>
				<CardTitle tag='h4' className='h4'>
					{EKHO_FINANCING_APPLICATION_SECTIONS.ADDITIONAL_INFO}
				</CardTitle>
			</CardLabel>
			{!fieldsToHide.includes(CREDIT_APP_FIELD_IDS.NOTE_TO_LENDER) && (
				<div className='col-12'>
					<FormGroup
						id='noteToLender'
						label='Note to the lender (optional)'
						formText='If you have any additional information or circumstances you would like to share with the underwriters, please enter it here (e.g. context regarding your credit history)'>
						<Textarea
							value={detailsFormik.values.noteToLender}
							onChange={detailsFormik.handleChange}
							onBlur={setOnBlurFunc(detailsFormik)}
							isValid={detailsFormik.isValid}
							isTouched={detailsFormik.touched.noteToLender}
							invalidFeedback={detailsFormik.errors.noteToLender}
							placeholder='Type your message here'
							size='lg'
						/>
					</FormGroup>
				</div>
			)}
		</>
	);
};

ApplicantDetailsForm.propTypes = {
	// eslint-disable-next-line react/forbid-prop-types
	detailsFormik: PropTypes.object.isRequired,
	// eslint-disable-next-line react/forbid-prop-types
	employmentFormik: PropTypes.object.isRequired,
	isCoApplicant: PropTypes.bool.isRequired,
	mapsKey: PropTypes.string.isRequired,
	mobileView: PropTypes.bool.isRequired,
	includePersonalReferences: PropTypes.bool,
	includeLoanConfig: PropTypes.bool,
	customOnBlurFunc: PropTypes.func,
	fieldsToHide: PropTypes.arrayOf(PropTypes.string),
	// setFinanceModalOpen: PropTypes.func,
	minDownPayment: PropTypes.number.isRequired,
	maxDownPayment: PropTypes.number.isRequired,
	allFieldsFullWidth: PropTypes.bool.isRequired,
	// object with monthlyPayment, term, apr, downPayment, disclaimerText
	financingProgram: PropTypes.shape({
		monthlyPayment: PropTypes.number,
		term: PropTypes.number,
		apr: PropTypes.number,
		downPayment: PropTypes.number,
		disclaimerText: PropTypes.string,
	}),
	vehiclePrice: PropTypes.number,
};
ApplicantDetailsForm.defaultProps = {
	includePersonalReferences: false,
	includeLoanConfig: false,
	customOnBlurFunc: null,
	fieldsToHide: [],
	financingProgram: null,
	vehiclePrice: null,
	// setFinanceModalOpen: null,
};

export default ApplicantDetailsForm;
